import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  UploadCloud,
  FileText,
  Image as ImageIcon,
  X,
  ShieldCheck,
  Loader2,
  MessageCircle,
  CheckCircle2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RX } from "@/constants/testIds";
import { fadeUp, viewport } from "@/lib/motion";
import { BUSINESS } from "@/data/site";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;
const ACCEPT = {
  "image/*": [".png", ".jpg", ".jpeg", ".webp", ".heic"],
  "application/pdf": [".pdf"],
};

const prettySize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export const Prescription = () => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null); // { waUrl, fileUrl, filename }

  const buildWaUrl = (id, nm, mob, nt, fname) => {
    const fileUrl = `${BACKEND}/api/prescriptions/${id}/file`;
    const lines = [
      "Hello Paliwal Medicos, I have uploaded my prescription.",
      "",
      `Name: ${nm}`,
      `Mobile: ${mob}`,
    ];
    if (nt) lines.push(`Note: ${nt}`);
    lines.push("", `Prescription file (${fname}):`, fileUrl);
    const text = lines.join("\n");
    return {
      fileUrl,
      waUrl: `https://wa.me/${BUSINESS.whatsapp}?text=${encodeURIComponent(text)}`,
    };
  };

  const onDrop = useCallback((accepted, rejected) => {
    if (rejected?.length) {
      toast.error("Please upload an image or PDF under 8 MB.");
      return;
    }
    if (accepted?.[0]) setFile(accepted[0]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPT,
    maxFiles: 1,
    maxSize: 8 * 1024 * 1024,
  });

  const submit = async () => {
    if (!file) return toast.error("Please attach your prescription file.");
    if (name.trim().length < 2) return toast.error("Please enter your name.");
    if (mobile.trim().length < 6) return toast.error("Please enter a valid mobile number.");

    const nm = name.trim();
    const mob = mobile.trim();
    const nt = note.trim();
    const fname = file.name;

    const fd = new FormData();
    fd.append("file", file);
    fd.append("name", nm);
    fd.append("mobile", mob);
    fd.append("note", nt);

    try {
      setLoading(true);
      const res = await axios.post(`${API}/prescriptions`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const id = res.data?.id;
      const { waUrl, fileUrl } = buildWaUrl(id, nm, mob, nt, fname);

      // Try to auto-open WhatsApp with the prescription link to the pharmacy.
      const opened = window.open(waUrl, "_blank", "noopener,noreferrer");
      if (opened) {
        toast.success("Prescription uploaded — opening WhatsApp to send to the pharmacy.");
      } else {
        toast.success("Prescription uploaded. Tap the WhatsApp button to send it to the pharmacy.");
      }
      setSent({ waUrl, fileUrl, filename: fname });
      setFile(null);
      setName("");
      setMobile("");
      setNote("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isPdf = file?.type === "application/pdf";

  return (
    <section id="prescription" className="relative bg-white py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            variants={fadeUp}
          >
            <p className="font-serif-accent italic text-lg text-brand-green">
              Skip the queue
            </p>
            <h2 className="mt-3 font-heading font-bold tracking-tight text-ink text-3xl sm:text-4xl lg:text-5xl leading-tight">
              Upload your prescription securely.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-slate-600 leading-relaxed">
              Send us an image or PDF of your doctor's prescription and we'll
              prepare your order before you arrive. Quick, private and hassle-free.
            </p>
            <div className="mt-4 rounded-2xl border border-brand-green/20 bg-brand-green/5 p-4 text-sm text-brand-green-dark leading-relaxed">
              <p className="font-semibold text-brand-green-dark">How it works</p>
              <p className="mt-1 text-slate-600">
                After you upload, WhatsApp will open with your prescription link ready to send to Paliwal Medicos ({BUSINESS.phoneDisplay}). Just tap <span className="font-semibold text-brand-green-dark">Send</span> and we'll get it instantly.
              </p>
            </div>
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-green/10 px-4 py-2 text-sm font-medium text-brand-green-dark">
              <ShieldCheck className="h-4 w-4" />
              Your files are handled privately
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={viewport}
            variants={fadeUp}
            className="rounded-3xl border border-slate-100 bg-surface p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
          >
            {sent && (
              <div
                data-testid="prescription-success-card"
                className="mb-5 rounded-2xl border border-brand-green/30 bg-brand-green/5 p-5"
              >
                <div className="flex items-start gap-3">
                  <span className="grid place-items-center h-10 w-10 rounded-full bg-brand-green text-white shrink-0">
                    <CheckCircle2 className="h-5 w-5" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-semibold text-ink">
                      Uploaded successfully
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      Now tap the button below to send your prescription link to
                      the pharmacy on WhatsApp.
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <a
                        data-testid="prescription-send-whatsapp-btn"
                        href={sent.waUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full bg-brand-green px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-green/25 transition-all hover:bg-brand-green-dark active:scale-95"
                      >
                        <MessageCircle className="h-4 w-4" /> Send on WhatsApp
                      </a>
                      <a
                        href={sent.fileUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-ink transition-all hover:border-brand-blue hover:text-brand-blue active:scale-95"
                      >
                        View file
                      </a>
                    </div>
                  </div>
                  <button
                    onClick={() => setSent(null)}
                    className="grid place-items-center h-8 w-8 rounded-full text-slate-400 hover:bg-slate-100 hover:text-ink transition-colors"
                    aria-label="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {!file ? (
              <div
                {...getRootProps()}
                data-testid={RX.dropzone}
                className={`flex flex-col items-center justify-center rounded-2xl border-2 border-dashed px-6 py-12 text-center cursor-pointer transition-colors ${
                  isDragActive
                    ? "border-brand-green bg-brand-green/5"
                    : "border-slate-300 bg-white hover:border-brand-blue hover:bg-brand-blue/5"
                }`}
              >
                <input {...getInputProps()} />
                <span className="grid place-items-center h-14 w-14 rounded-2xl bg-brand-blue/10 text-brand-blue">
                  <UploadCloud className="h-7 w-7" />
                </span>
                <p className="mt-4 font-heading font-semibold text-ink">
                  {isDragActive ? "Drop it here…" : "Drag & drop, or click to browse"}
                </p>
                <p className="mt-1 text-sm text-slate-500">
                  JPG, PNG, WEBP or PDF · up to 8 MB
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="grid place-items-center h-11 w-11 rounded-xl bg-brand-green/10 text-brand-green-dark shrink-0">
                    {isPdf ? <FileText className="h-5 w-5" /> : <ImageIcon className="h-5 w-5" />}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink">{file.name}</p>
                    <p className="text-xs text-slate-500">{prettySize(file.size)}</p>
                  </div>
                </div>
                <button
                  data-testid={RX.removeBtn}
                  onClick={() => setFile(null)}
                  className="grid place-items-center h-9 w-9 rounded-full text-slate-400 hover:bg-slate-100 hover:text-ink transition-colors"
                  aria-label="Remove file"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}

            <div className="mt-5 grid gap-4">
              <Input
                data-testid={RX.nameInput}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                className="h-12 rounded-xl border-slate-200 bg-white focus-visible:ring-2 focus-visible:ring-brand-blue/25 focus-visible:border-brand-blue"
              />
              <Input
                data-testid={RX.mobileInput}
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
                inputMode="tel"
                placeholder="Mobile number"
                className="h-12 rounded-xl border-slate-200 bg-white focus-visible:ring-2 focus-visible:ring-brand-blue/25 focus-visible:border-brand-blue"
              />
              <Textarea
                data-testid={RX.noteInput}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Any note for us (optional)"
                rows={2}
                className="rounded-xl border-slate-200 bg-white focus-visible:ring-2 focus-visible:ring-brand-blue/25 focus-visible:border-brand-blue"
              />
              <button
                data-testid={RX.submitBtn}
                onClick={submit}
                disabled={loading}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-7 py-4 text-sm font-semibold text-white shadow-lg shadow-brand-blue/25 transition-all hover:bg-brand-blue-dark active:scale-95 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Uploading…
                  </>
                ) : (
                  <>
                    <UploadCloud className="h-4 w-4" /> Send Prescription
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
